import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { Presupuesto, Categoria, Transaccion } from '../types';
import { computa } from './transaccionUtils';
import { NOMBRE_APP } from '../config/marca';
import { guardarPDF } from './plataforma';
import { limiteEnPeriodo } from './presupuestoUtils';

// ── Informe de gastos/ingresos por categoría ──────────────────────────────────

export interface FilaInforme {
  nombre: string;
  icono: string;
  actual: number;
  anterior: number;
  diff: number;
}

export function exportarInformesPDF(
  filas: FilaInforme[],
  tipoTx: 'gasto' | 'ingreso',
  periodoActualLabel: string,
  periodoAntLabel: string,
  totalActual: number,
  totalAnterior: number,
  totalDiff: number,
): void {
  const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
  const color: [number, number, number] = tipoTx === 'gasto' ? [220, 38, 38] : [22, 163, 74];

  // Cabecera
  doc.setFillColor(...color);
  doc.rect(0, 0, 210, 30, 'F');
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(18);
  doc.setFont('helvetica', 'bold');
  doc.text(`${NOMBRE_APP} — Informe de ${tipoTx === 'gasto' ? 'Gastos' : 'Ingresos'}`, 14, 13);
  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');
  doc.text(`${periodoActualLabel} vs ${periodoAntLabel}`, 14, 23);
  doc.text(`Generado: ${new Date().toLocaleDateString('es-ES')}`, 148, 23);

  if (filas.length === 0) {
    doc.setTextColor(100, 100, 100);
    doc.setFontSize(12);
    doc.text('No hay datos para este período.', 14, 50);
    guardarPDF(doc, `informe_${tipoTx}_${periodoActualLabel}.pdf`);
    return;
  }

  autoTable(doc, {
    startY: 36,
    head: [['Categoría', periodoActualLabel, periodoAntLabel, 'Diferencia']],
    body: filas.map(f => [
      `${f.icono} ${f.nombre}`,
      `€${f.actual.toFixed(2)}`,
      `€${f.anterior.toFixed(2)}`,
      `${f.diff >= 0 ? '+' : ''}€${f.diff.toFixed(2)}`,
    ]),
    headStyles: { fillColor: color, textColor: 255, fontStyle: 'bold', fontSize: 9 },
    bodyStyles: { fontSize: 9 },
    columnStyles: {
      0: { cellWidth: 70 },
      1: { cellWidth: 36, halign: 'right' },
      2: { cellWidth: 36, halign: 'right' },
      3: { cellWidth: 36, halign: 'right' },
    },
    didParseCell(data) {
      if (data.section === 'body' && data.column.index === 3) {
        const val = parseFloat((data.cell.raw as string).replace('€', '').replace('+', ''));
        if (!isNaN(val)) {
          const mala = tipoTx === 'gasto' ? val > 0 : val < 0;
          if (mala) data.cell.styles.textColor = [185, 28, 28];
          else if (Math.abs(val) > 0.01) data.cell.styles.textColor = [21, 128, 61];
        }
      }
    },
    alternateRowStyles: { fillColor: [241, 245, 249] },
    margin: { left: 14, right: 14 },
  });

  // Resumen total
  const finalY = (doc as any).lastAutoTable.finalY + 8;
  doc.setFillColor(241, 245, 249);
  doc.roundedRect(14, finalY, 182, 18, 3, 3, 'F');
  doc.setTextColor(30, 30, 30);
  doc.setFontSize(9);
  doc.setFont('helvetica', 'bold');
  doc.text('TOTAL', 20, finalY + 7);
  doc.text(`€${totalActual.toFixed(2)}`, 84 + 36 - 14, finalY + 7, { align: 'right' });
  doc.text(`€${totalAnterior.toFixed(2)}`, 120 + 36 - 14, finalY + 7, { align: 'right' });
  const diffStr = `${totalDiff >= 0 ? '+' : ''}€${totalDiff.toFixed(2)}`;
  const diffMala = tipoTx === 'gasto' ? totalDiff > 0 : totalDiff < 0;
  doc.setTextColor(diffMala ? 185 : Math.abs(totalDiff) < 0.01 ? 100 : 21,
                  diffMala ? 28 : Math.abs(totalDiff) < 0.01 ? 100 : 128,
                  diffMala ? 28 : Math.abs(totalDiff) < 0.01 ? 100 : 61);
  doc.text(diffStr, 196 - 14, finalY + 7, { align: 'right' });
  doc.setFont('helvetica', 'normal');
  doc.text(`${filas.length} categoría(s)`, 20, finalY + 13);

  doc.setFontSize(8);
  doc.setTextColor(150, 150, 150);
  doc.text(`${NOMBRE_APP} — Exportado automáticamente`, 14, 290);
  guardarPDF(doc, `informe_${tipoTx}_${periodoActualLabel.replace(/ /g, '_')}.pdf`);
}

function calcularGasto(
  presupuesto: Presupuesto,
  periodicidad: 'mensual' | 'anual',
  periodo: string,
  transacciones: Transaccion[],
): number {
  return transacciones
    .filter(t => {
      if (t.tipo !== 'gasto' || !computa(t)) return false;
      // Modelo evergreen: emparejamiento directo por id de presupuesto
      if (t.presupuestoId !== presupuesto.id) return false;
      const fecha = new Date(t.fecha);
      if (periodicidad === 'mensual') {
        return `${fecha.getFullYear()}-${String(fecha.getMonth() + 1).padStart(2, '0')}` === periodo;
      } else {
        return String(fecha.getFullYear()) === periodo;
      }
    })
    .reduce((acc, t) => acc + t.cantidad, 0);
}

export function exportarPresupuestosPDF(
  presupuestos: Presupuesto[],
  categorias: Categoria[],
  transacciones: Transaccion[],
  periodo: string,     // YYYY-MM o YYYY
  periodicidad: 'mensual' | 'anual'
): void {
  const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });

  // ── Cabecera ────────────────────────────────────────────────────────────────
  doc.setFillColor(37, 99, 235); // blue-600
  doc.rect(0, 0, 210, 30, 'F');

  doc.setTextColor(255, 255, 255);
  doc.setFontSize(20);
  doc.setFont('helvetica', 'bold');
  doc.text(`${NOMBRE_APP} — Resumen de Presupuestos`, 14, 13);

  doc.setFontSize(11);
  doc.setFont('helvetica', 'normal');
  const etiquetaPeriodo = periodicidad === 'mensual'
    ? `Período: ${periodo}`
    : `Año: ${periodo}`;
  doc.text(etiquetaPeriodo, 14, 23);
  doc.text(`Generado: ${new Date().toLocaleDateString('es-ES')}`, 140, 23);

  // ── Filtrar presupuestos del período ────────────────────────────────────────
  // Modelo evergreen: un repetitivo aplica desde su inicio (p.mes) en adelante;
  // un puntual solo en su período exacto.
  const lista = presupuestos.filter(p =>
    p.periodicidad === periodicidad &&
    (p.repetitivo ? p.mes <= periodo : p.mes === periodo)
  );

  if (lista.length === 0) {
    doc.setTextColor(100, 100, 100);
    doc.setFontSize(12);
    doc.text('No hay presupuestos para este período.', 14, 50);
    guardarPDF(doc, `presupuestos_${periodo}.pdf`);
    return;
  }

  // ── Tabla ───────────────────────────────────────────────────────────────────
  const getNombreCat = (id: string) => {
    const c = categorias.find(c => c.id === id);
    return c ? c.nombre : 'Desconocida';
  };

  let totalLimite = 0;
  let totalGasto = 0;

  const filas = lista.map(p => {
    const gasto = calcularGasto(p, p.periodicidad, periodo, transacciones);
    // El importe de este período (un mensual puede cambiar de un mes a otro)
    const limite = limiteEnPeriodo(p, periodo);
    const porcentaje = limite > 0 ? (gasto / limite) * 100 : 0;
    const excedido = gasto > limite;
    totalLimite += limite;
    totalGasto += gasto;

    return [
      p.nombre,
      getNombreCat(p.categoriaId),
      `€${limite.toFixed(2)}`,
      `€${gasto.toFixed(2)}`,
      `${Math.min(porcentaje, 999).toFixed(0)}%`,
      excedido ? `⚠ +€${(gasto - limite).toFixed(2)}` : 'OK',
      p.repetitivo ? 'Sí' : 'No',
    ];
  });

  autoTable(doc, {
    startY: 36,
    head: [['Presupuesto', 'Categoría', 'Límite', 'Gastado', '%', 'Estado', 'Repetitivo']],
    body: filas,
    headStyles: {
      fillColor: [37, 99, 235],
      textColor: 255,
      fontStyle: 'bold',
      fontSize: 9,
    },
    bodyStyles: { fontSize: 9 },
    columnStyles: {
      0: { cellWidth: 38 },
      1: { cellWidth: 32 },
      2: { cellWidth: 22, halign: 'right' },
      3: { cellWidth: 22, halign: 'right' },
      4: { cellWidth: 14, halign: 'center' },
      5: { cellWidth: 28, halign: 'center' },
      6: { cellWidth: 18, halign: 'center' },
    },
    didParseCell(data) {
      // Filas excedidas en rojo claro
      if (data.section === 'body') {
        const estado = data.row.cells[5]?.raw as string;
        if (estado && estado.startsWith('⚠')) {
          data.cell.styles.textColor = [185, 28, 28];
          data.cell.styles.fontStyle = 'bold';
        }
      }
    },
    alternateRowStyles: { fillColor: [241, 245, 249] },
    margin: { left: 14, right: 14 },
  });

  // ── Resumen total ────────────────────────────────────────────────────────────
  const finalY = (doc as any).lastAutoTable.finalY + 8;
  const porcentajeTotal = totalLimite > 0 ? (totalGasto / totalLimite) * 100 : 0;
  const excedidoTotal = totalGasto > totalLimite;

  doc.setFillColor(excedidoTotal ? 254 : 239, excedidoTotal ? 226 : 246, excedidoTotal ? 226 : 255);
  doc.roundedRect(14, finalY, 182, 20, 3, 3, 'F');

  doc.setTextColor(excedidoTotal ? 185 : 37, excedidoTotal ? 28 : 99, excedidoTotal ? 28 : 235);
  doc.setFontSize(10);
  doc.setFont('helvetica', 'bold');
  doc.text('TOTAL', 20, finalY + 8);
  doc.text(`Límite: €${totalLimite.toFixed(2)}`, 55, finalY + 8);
  doc.text(`Gastado: €${totalGasto.toFixed(2)}`, 105, finalY + 8);
  doc.text(`${porcentajeTotal.toFixed(0)}%`, 160, finalY + 8);

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  doc.setTextColor(100, 100, 100);
  doc.text(`${lista.length} presupuesto(s)`, 20, finalY + 15);

  // ── Pie de página ────────────────────────────────────────────────────────────
  doc.setFontSize(8);
  doc.setTextColor(150, 150, 150);
  doc.text(`${NOMBRE_APP} — Exportado automáticamente`, 14, 290);

  guardarPDF(doc, `presupuestos_${periodo}.pdf`);
}
